import { Button } from '@/components/Button';
import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { AddressDisplay } from '@/components/AddressDisplay';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { getFormattedDate, getFormattedTime } from '@/utils/date.utils';
import { StatusTag } from '@/components/Tag/StatusTag';
import {
  getInspectionProgressStatusLabel,
  getInspectionSubProgressStatus,
} from '@/utils/vehicule-check';
import { useState, useEffect } from 'react';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { useUserRole } from '@/hooks/auth.hooks';
import { ViewVehiculeCheckTab } from '@/type/ui/tab/vehiculeCheckTab';
import { ResultPhotoTab } from '@/type/ui/tab/vehiculeCheckResultTab';
import { getResultByVehiculeCheckId } from '@/api/vehicule-check-result/vehicule-check-result';

export const VehiculeCheckTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const { isAdmin, isModerator } = useUserRole();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [resultDataLoading, setResultDataLoading] = useState(false);
  const [resultData, setResultData] = useState<
    Record<string, { vin: string; licensePlateNumber: string }>
  >({});

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  useEffect(() => {
    const fetchResultData = async () => {
      // Set loading result data
      setResultDataLoading(true);
      // Clear previous data
      setResultData({});

      // Fetch each entry and update state immediately when data comes back
      data.forEach(async (entry) => {
        try {
          const response = await getResultByVehiculeCheckId(entry?.id);
          const resultInfo = {
            licensePlateNumber: response?.data?.valueImmatriculation || '',
            vin: response?.data?.valueVin || '',
          };

          // Update state immediately for this entry
          setResultData((prev) => ({
            ...prev,
            [entry?.id]: resultInfo,
          }));
        } catch {
          // Even on error, update state so component knows this entry was processed
          setResultData((prev) => ({
            ...prev,
            [entry?.id]: {
              vin: '',
              licensePlateNumber: '',
            },
          }));
        } finally {
          // Set loading result data to false
          setResultDataLoading(false);
        }
      });
    };

    if (data && data.length > 0) {
      fetchResultData();
    }
  }, [data]);

  const handleToggleIndividualDetails = (id: string) => {
    if (selectedIds.includes(id)) {
      const ids = selectedIds.filter((selectedId) => selectedId != id);
      setSelectedIds(ids);
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={8}
          text={numberOfItemsText}
          disableDownload
          onDeactivate={isAdmin ? handleDeactivate : undefined}
          onDownload={() => {}}
          onCreate={() => navigate('/vehicule-check/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const currentResultData = resultData?.[entry?.id];

        const isIndividual =
          entry?.actionUserType === ActionUserType.INDIVIDUAL;

        const isToModerate =
          entry?.humanModerationStatus === HumanModerationStatus.A_MODERER;

        const isSubProgressStatusCompleted = entry?.progressStatus
          ?.toLowerCase()
          .includes('completed_');

        const subProgressStatus = getInspectionSubProgressStatus({
          t,
          status: entry?.progressStatus,
        });

        return (
          <tr key={entry?.id}>
            {!isModerator && (
              <TableCell
                tdClassName="sticky-select-column"
                content={
                  <Checkbox
                    checked={Boolean(
                      selectedTableData?.find((item) => item?.id === entry?.id)
                    )}
                    onChange={() => onSelectedItem(entry?.id)}
                  />
                }
              />
            )}
            <TableCell
              tdClassName={twMerge('', !isModerator && '!px-2.5')}
              content={
                <div className="text-[13.7px] break-all">
                  {entry?.referenceNumber || '--'}
                </div>
              }
            />
            <TableCell
              content={
                <div className="flex flex-col">
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-calendar text-md" />
                    </div>
                    <div>{getFormattedDate(entry?.updatedAt)}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-time text-md" />
                    </div>
                    <div>{getFormattedTime(entry?.updatedAt)}</div>
                  </div>
                </div>
              }
            />
            <TableCell
              content={
                resultDataLoading ? (
                  <div className="text-sm">{`${t('common:loading')}...`}</div>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <StatusTag
                        text="123"
                        className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                      />
                      <div className="mt-[0.5px] font-medium">
                        {currentResultData?.licensePlateNumber ||
                          entry?.vehicule?.licensePlateNumber ||
                          '--'}
                      </div>
                    </div>
                    <div className="mt-2 flex gap-2">
                      <StatusTag
                        text="VIN"
                        className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                      />
                      <div className="mt-[0.5px] break-all">
                        {currentResultData?.vin || entry?.vehicule?.vin || '--'}
                      </div>
                    </div>
                  </>
                )
              }
            />
            <TableCell
              content={
                <div
                  className={twMerge(
                    'px-1 bg-slate-50 py-2.75 rounded-lg',
                    isIndividual && 'max-h-[45px] overflow-y-hidden',
                    selectedIds.includes(entry?.id) &&
                      'max-h-none overflow-y-auto'
                  )}
                >
                  <div className="flex justify-center items-center">
                    <p className="px-3.5 font-semibold text-[13.5px] text-center uppercase">
                      {isIndividual
                        ? t('common:individual')
                        : t('common:professional')}
                    </p>
                    {isIndividual && (
                      <div className="-mt-[1.7px]">
                        <Button
                          buttonClassName="px-1 h-7 rounded-lg shadow-md"
                          leftIcon={
                            <i
                              className={twMerge(
                                'ml-[0.5px] -mt-[1px] ki-outline ki-up-square text-white !text-lg transition-all ease-linear duration-75',
                                selectedIds.includes(entry?.id) &&
                                  'rotate-180 mt-[1px]'
                              )}
                            />
                          }
                          onClick={() =>
                            handleToggleIndividualDetails(entry?.id)
                          }
                        />
                      </div>
                    )}
                  </div>
                  {isIndividual && (
                    <div className="mt-3 mx-1.5 flex flex-col gap-[5px] text-[12.5px] pt-[16px] pb-[13px] px-1.5 break-words border border-dashed border-slate-300 rounded-lg">
                      {(entry?.individualFirstname ||
                        entry?.individualLastname) && (
                        <div className="mb-[1.5px] flex gap-1 leading-[16px]">
                          <div className="flex justify-center items-center h-0 -mt-[2.45px]">
                            <i className="h-0 ki-outline ki-user-tick text-[19px]" />
                          </div>
                          {entry?.individualFirstname && (
                            <div className='"first-letter:uppercase"'>
                              <span>{entry?.individualFirstname}</span>{' '}
                              <span>{entry?.individualLastname}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {entry?.individualEmail && (
                        <div className="my-1 flex gap-1">
                          <div className="flex justify-center items-center h-0 -mt-[3px]">
                            <i className="h-0 ki-outline ki-paper-clip text-[17.5px]" />
                          </div>
                          <div className="break-all leading-[14px]">
                            {entry?.individualEmail}
                          </div>
                        </div>
                      )}
                      {entry?.individualPhone && (
                        <div className="flex gap-1">
                          <div className="flex justify-center items-center h-0">
                            <i className="h-0 ki-outline ki-phone text-[16px]" />
                          </div>
                          <div>{entry?.individualPhone}</div>
                        </div>
                      )}
                      {entry?.individualAddressLine1 && (
                        <div className="mt-1 flex gap-1">
                          <div className="flex justify-center items-center h-0">
                            <i className="h-0 ki-outline ki-geolocation text-[17px]" />
                          </div>
                          <div className="leading-[16px]">
                            {AddressDisplay({
                              addressLine: entry?.individualAddressLine1,
                              postalCode: entry?.individualPostalCode,
                              city: entry?.individualCity,
                              country: entry?.individualCountry,
                            })}
                          </div>
                        </div>
                      )}
                      {entry?.individualAddressLine2 && (
                        <div className="mt-1 flex gap-1">
                          <div className="flex justify-center items-center h-0">
                            <i className="h-0 ki-outline ki-geolocation text-[17px]" />
                          </div>
                          <div className="leading-[16px]">
                            {AddressDisplay({
                              addressLine: entry?.individualAddressLine2,
                              postalCode: entry?.individualPostalCode,
                              city: entry?.individualCity,
                              country: entry?.individualCountry,
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              }
            />
            {/* <TableCell
              tdClassName="text-center"
              content={
                <div className="inline-flex font-semibold px-1.5 py-[2px] rounded-md bg-slate-100">
                  0
                </div>
              }
            /> */}
            <TableCell
              tdClassName="!px-2.5 text-center"
              content={
                <StatusTag
                  text={`${
                    isToModerate
                      ? t('common:toModerate')
                      : t('common:moderated_feminine')
                  }`}
                  className={
                    isToModerate
                      ? 'bg-red-FF2929/10 text-red-FF2929'
                      : 'bg-green-EAFFF2 border-green-C9F4D2 py-[3px] text-green-11BFA4 text-[12.7px] tracking-[0.005em]'
                  }
                />
              }
            />
            <TableCell
              tdClassName="text-center !px-2.5"
              content={
                <>
                  <StatusTag
                    className="bg-purple-EFEFFD py-[6px] px-[7.5px] text-indigo-600 leading-4"
                    text={
                      getInspectionProgressStatusLabel({
                        t,
                        status: entry?.progressStatus,
                      }) || '--'
                    }
                  />
                  {isSubProgressStatusCompleted && (
                    <div className="mt-0.75 flex justify-center">
                      <div
                        className={twMerge(
                          'text-xs text-purple-5433FF/60 font-medium',
                          subProgressStatus?.className
                        )}
                      >
                        {subProgressStatus?.text}
                      </div>
                    </div>
                  )}
                </>
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu disabled={pendingDeactivate}>
                  {!isAdmin && isModerator && (
                    <SubMenuItem
                      text="Modérer"
                      leftIconClassName="ki-toggle-on !text-[17.5px]"
                      to={`/vehicule-check/view/${entry?.id}?tab=${ViewVehiculeCheckTab.DETAILED_RESULTS}&childTab=${ResultPhotoTab.EQUIPMENTS}`}
                    />
                  )}
                  {!isModerator && (
                    <>
                      <SubMenuItem
                        text={t('common:view')}
                        leftIconClassName="ki-eye !text-[17.5px]"
                        to={`/vehicule-check/view/${entry?.id}`}
                      />
                      {isIndividual && (
                        <SubMenuItem
                          text={t('common:changeRequest')}
                          leftIconClassName="ki-paper-plane !text-[16px]"
                          to={`/vehicule-check/request/${entry?.id}`}
                        />
                      )}
                      <SubMenuItem
                        text={t('common:edit')}
                        leftIconClassName="ki-notepad-edit !text-[16px]"
                        to={`/vehicule-check/edit/${entry?.id}`}
                      />
                      {isAdmin && (
                        <SubMenuItem
                          text={t('common:delete')}
                          disabled={pendingDeactivate}
                          leftIconClassName="ki-trash !text-[19px]"
                          textClassName="line-clamp-1"
                          onClick={() => onDeactivate([entry])}
                        />
                      )}
                    </>
                  )}
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
